import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { AutomationNodeType } from '@frontend/app/types/globalTypes';
import { EmptyStateButton } from './shared/EmptyStateButton';
import { NodeToolbarV2 } from './shared/NodeToolbarV2';
import { Button } from '@/shadcn/components/ui/button';
import { FlowNodeData } from '../types';
import { cn } from '@/shadcn/lib/utils';
import { NodeIcon } from '../utils/nodeIconMapping';
import { getDelayStateFromDuration } from '../utils/timeUtils';
import DeleteNodeButton from './shared/DeleteNodeButton';
import AddNodeButton from './shared/AddNodeButton';

interface DelayNodeV2Props {
  data: FlowNodeData;
}

const formatDelayTime = (durationMs?: number) => {
  if (durationMs === undefined || durationMs === null) return 'Not configured';

  // Handle minimal duration specifically (1ms for hidden root nodes)
  if (durationMs === 0) return 'No delay';

  // Use the same logic as the property panel to ensure consistency
  const { amount, unit } = getDelayStateFromDuration(durationMs);
  return `${amount} ${amount === 1 ? unit.slice(0, -1) : unit}`;
};

export const DelayNodeV2: React.FC<DelayNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    metadata,
    isEditing,
    isSelected,
    isRootNode,
    onNodeClick,
    onDeleteNode,
    onNodeResize,
  } = data;

  // Height measurement for dynamic spacing
  const nodeRef = useRef<HTMLDivElement>(null);

  const [showOptionsPopover, setShowOptionsPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(false);
    onDeleteNode?.(nodeId);
  };

  const handleOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(!showOptionsPopover);
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowOptionsPopover(false);
      }
    };

    if (showOptionsPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showOptionsPopover]);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open properties panel by selecting this node
    onNodeClick?.(nodeId);
  };

  const durationMs = metadata?.durationMs;
  const isConfigured = durationMs !== undefined && durationMs !== null;

  // Height measurement effect for dynamic spacing
  useLayoutEffect(() => {
    if (nodeRef.current && onNodeResize) {
      const measureHeight = () => {
        const height = nodeRef.current?.offsetHeight;
        if (height) {
          onNodeResize(nodeId, height);
        }
      };

      // Initial measurement
      measureHeight();

      // Create ResizeObserver for dynamic height changes
      const resizeObserver = new ResizeObserver(measureHeight);
      resizeObserver.observe(nodeRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [nodeId, onNodeResize, isConfigured, durationMs]); // Re-measure when configuration changes

  return (
    <div ref={nodeRef} className="relative">
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="w-0 h-0 opacity-0"
      />

      {/* Main node container */}
      <div className="flex flex-col items-start justify-center relative w-[342px] transition-all duration-200">
        {/* Node with border */}
        <div className={cn(
          'w-full bg-white border border-grey-2 rounded-2xl transition-all duration-200 pb-4',
          isSelected && 'border-grey-6 border-[1px] shadow-md shadow-black/10'
        )}
        >
          {/* Header Card */}
          <div
            className="w-full bg-white cursor-pointer rounded-t-2xl"
            onClick={handleNodeClick}
          >
            <div className="flex h-12 items-center gap-1 p-3 relative w-full">
              <div className="flex justify-between flex-1 self-stretch grow items-center relative">
                <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                  <NodeIcon node={{ type: AutomationNodeType.DELAY }} size="md" />
                  <div className="relative w-fit font-medium text-grey-6 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                    Time Delay
                  </div>
                </div>
                <div className="inline-flex gap-2 flex-[0_0_auto] items-center relative">
                  {!isRootNode && (
                    <div ref={popoverRef}>
                      <DeleteNodeButton handleDelete={handleDelete} showOptionsPopover={showOptionsPopover} setShowOptionsPopover={setShowOptionsPopover} handleOptionsClick={handleOptionsClick} isEditing={isEditing} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Card */}
          <div className="w-full bg-white rounded-b-2xl overflow-hidden">
            <div className="flex flex-col items-start gap-3 px-3 pb-3 relative w-full bg-white">
              {isConfigured ? (
                <div className="flex items-center justify-between px-3 py-2 relative self-stretch w-full flex-[0_0_auto] bg-grey-1 rounded-xl overflow-hidden">
                  <div className="text-xs font-medium text-grey-5 bg-grey-2 rounded-lg px-2 py-1">
                    Wait for {formatDelayTime(durationMs)}
                  </div>
                  {isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditClick}
                    aria-label="Edit delay"
                  >
                    <FontAwesomeIcon icon={faPen} className="w-3 h-3 text-grey-5" />
                  </Button>
                )}
                </div>
            ) : (
              <EmptyStateButton
                nodeType={AutomationNodeType.DELAY}
                onClick={handleEditClick}
              />
            )}
            </div>
          </div>
        </div>

        {/* Bottom connector with add button - positioned to show half inside border */}
        {isEditing && (
          <AddNodeButton data={data} nodeId={nodeId} />
        )}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="w-0 h-0 opacity-0"
      />

      {/* Node Toolbar */}
      {data.isToolbarVisible && (
        <NodeToolbarV2
          nodeId={nodeId}
          isVisible
          onAddNode={data.onAddChild}
          onClose={() => {
            data.onCloseToolbar?.();
            data.onShowDashedPlaceholder?.(null);
          }}
          position={Position.Right}
          offset={15}
        />
      )}
    </div>
  );
};
